import { useRef, useSyncExternalStore } from 'react'
import type { BoggleGameState } from '~/boggle/engine/standard/types'
import type { BoggleStore } from '~/boggle/ui/state/Store'

export type BoggleResetCooldownTracker = {
  cooldownStartMs: null | number
  intervalsRemainingSnapshot: null | number
  resetsRemainingSnapshot: null | number
}

export function createBoggleResetCooldownTracker(): BoggleResetCooldownTracker {
  return {
    cooldownStartMs: null,
    intervalsRemainingSnapshot: null,
    resetsRemainingSnapshot: null,
  }
}

export function getBoggleResetCooldownDelayMs(
  state: Pick<BoggleGameState, 'phase' | 'playgroundSettings'>,
) {
  if (
    state.phase !== 'playing' ||
    !state.playgroundSettings.skills.reset.enabled ||
    state.playgroundSettings.skills.reset.delaySeconds === null
  ) {
    return null
  }

  return state.playgroundSettings.skills.reset.delaySeconds * 1000
}

export function advanceBoggleResetCooldownTracker(
  tracker: BoggleResetCooldownTracker,
  {
    delayMs,
    inPrep,
    intervalsRemaining,
    resetsRemaining,
    timeMs,
  }: {
    delayMs: null | number
    inPrep: boolean
    intervalsRemaining: null | number
    resetsRemaining: number
    timeMs: number
  },
): BoggleResetCooldownTracker & { cooldownActive: boolean } {
  if (delayMs === null) {
    return {
      cooldownActive: false,
      cooldownStartMs: null,
      intervalsRemainingSnapshot: null,
      resetsRemainingSnapshot: null,
    }
  }

  if (inPrep) {
    return {
      cooldownActive: true,
      cooldownStartMs: null,
      intervalsRemainingSnapshot: intervalsRemaining,
      resetsRemainingSnapshot: resetsRemaining,
    }
  }

  let cooldownStartMs = tracker.cooldownStartMs
  if (cooldownStartMs === null || timeMs < cooldownStartMs) {
    cooldownStartMs = timeMs
  }

  if (
    tracker.resetsRemainingSnapshot !== null &&
    resetsRemaining < tracker.resetsRemainingSnapshot
  ) {
    cooldownStartMs = timeMs
  }

  if (
    tracker.intervalsRemainingSnapshot !== null &&
    intervalsRemaining !== null &&
    intervalsRemaining < tracker.intervalsRemainingSnapshot
  ) {
    cooldownStartMs = timeMs
  }

  return {
    cooldownActive: timeMs - cooldownStartMs < delayMs,
    cooldownStartMs,
    intervalsRemainingSnapshot: intervalsRemaining,
    resetsRemainingSnapshot: resetsRemaining,
  }
}

export function useBoggleResetCooldownActive(
  store: BoggleStore,
  state: Pick<
    BoggleGameState,
    | 'intervalPlayBeginsAtMs'
    | 'intervalsRemaining'
    | 'phase'
    | 'playgroundSettings'
    | 'resetsRemaining'
  >,
) {
  const trackerRef = useRef(createBoggleResetCooldownTracker())
  const delayMs = getBoggleResetCooldownDelayMs(state)

  const getSnapshot = () => {
    const timeMs = store.getTimeState().timeMs
    const inPrep =
      state.intervalPlayBeginsAtMs !== null && timeMs < state.intervalPlayBeginsAtMs
    const tracker = advanceBoggleResetCooldownTracker(trackerRef.current, {
      delayMs,
      inPrep,
      intervalsRemaining: state.intervalsRemaining,
      resetsRemaining: state.resetsRemaining,
      timeMs,
    })
    trackerRef.current = {
      cooldownStartMs: tracker.cooldownStartMs,
      intervalsRemainingSnapshot: tracker.intervalsRemainingSnapshot,
      resetsRemainingSnapshot: tracker.resetsRemainingSnapshot,
    }
    return tracker.cooldownActive
  }

  return useSyncExternalStore(store.subscribeTime, getSnapshot, getSnapshot)
}
